// Import/export delle rose in CSV, in un posto solo.
//
// Prima esisteva in tre copie quasi identiche, con `row.split(',')` nudo e un
// separatore sentinella `$,$,$` che veniva scritto e poi esplicitamente saltato
// in lettura. Qualunque nome contenente una virgola corrompeva il file.

const HEADER = ['participant', 'team_name', 'player_id', 'player_name', 'role', 'price'];

function quote(value) {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Parser CSV conforme a RFC 4180: gestisce virgolette, virgole e a capo nei campi. */
export function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];

        if (inQuotes) {
            if (char === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1;
                } else {
                    inQuotes = false;
                }
            } else {
                field += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === ',') {
            row.push(field);
            field = '';
        } else if (char === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else if (char !== '\r') {
            field += char;
        }
    }

    if (field !== '' || row.length) {
        row.push(field);
        rows.push(row);
    }

    return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}

/**
 * Serializza le rose. `teams` e una mappa nome -> {budget, roster}; `catalogue`
 * mappa player_id -> record del listone e serve solo per la leggibilita.
 */
export function encodeTeams(teams, catalogue = {}) {
    const lines = [HEADER.join(',')];

    Object.entries(teams ?? {}).forEach(([participant, team]) => {
        const roster = team?.roster ?? team?.players ?? [];
        roster.forEach((entry) => {
            const player = catalogue[entry.player_id] ?? {};
            lines.push(
                [
                    participant,
                    team?.imported_name ?? participant,
                    entry.player_id,
                    player['Nome Completo'] ?? '',
                    entry.role ?? player.Ruolo ?? '',
                    entry.purchase_price,
                ]
                    .map(quote)
                    .join(','),
            );
        });
    });

    return lines.length > 1 ? `${lines.join('\n')}\n` : '';
}

/**
 * Legge sia il formato nuovo sia quello storico `$,$,$` + `nome,id,prezzo`:
 * gli utenti hanno gli export della stagione scorsa e devono poterli riusare.
 */
export function parseTeams(text, catalogue = {}) {
    const rows = parseCsv(text);
    if (!rows.length) return {};

    const first = rows[0].map((cell) => cell.trim().toLowerCase());
    const isNewFormat = first[0] === 'participant' && first.includes('player_id');
    const body = isNewFormat ? rows.slice(1) : rows;

    const teams = {};
    let current = null;

    body.forEach((cells) => {
        if (cells[0] === '$' && cells[1] === '$' && cells[2] === '$') return;

        const participant = (cells[0] ?? '').trim();
        const teamName = isNewFormat ? (cells[1] ?? '').trim() : participant;
        const playerId = Number.parseInt(isNewFormat ? cells[2] : cells[1], 10);
        const price = Number.parseInt(isNewFormat ? cells[5] : cells[2], 10);

        if (participant) current = participant;
        if (!current) return;

        if (!teams[current]) {
            teams[current] = { imported_name: teamName || current, roster: [] };
        }

        if (!Number.isFinite(playerId) || !Number.isFinite(price)) return;
        if (teams[current].roster.some((entry) => entry.player_id === playerId)) return;

        const player = catalogue[playerId];
        const role = isNewFormat ? (cells[4] || '').trim().toUpperCase() : null;
        teams[current].roster.push({
            player_id: playerId,
            role: player?.Ruolo ?? role ?? null,
            purchase_price: price,
        });
    });

    return teams;
}

/** Scarica una stringa come file, liberando poi l'URL creato. */
export function downloadCsv(content, filename = 'squadre.csv') {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Mai chiamato nelle tre copie precedenti: ogni export perdeva memoria.
    URL.revokeObjectURL(url);
}
